import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./AdminDashboard.css";

// context
import { AdminDashboardContext } from "../../../context/AdminDashboardContext";

// my components
import LoadingSpinner from "../../InputComponents/LoadingSpinner/LoadingSpinner";
import ModalMessage from "../../InputComponents/ModalMessage/ModalMessage";
import ModalApproveProject from "./AdminDashboardComponents/ModalApproveProject/ModalApproveProject";
import ModalDeclineProject from "./AdminDashboardComponents/ModalDeclineProject/ModalDeclineProject";
import ModalDiscardChanges from "./AdminDashboardComponents/ModalDiscardChanges/ModalDiscardChanges";
import ModalSettings from "./AdminDashboardComponents/ModalSettings/ModalSettings";
import ModalUpdateProjectInfo from "./AdminDashboardComponents/ModalUpdateProjectInfo/ModalUpdateProjectInfo";
import ProjectInfo from "./AdminDashboardComponents/ProjectInfo/ProjectInfo";
import ProjectInfoButtonContainer from "./AdminDashboardComponents/ProjectInfoButtonContainer/ProjectInfoButtonContainer";
import AdminDashboardHeader from "./AdminDashboardHeader/AdminDashboardHeader";
// external components

// FontAwesome
import {
  faDownload,
  faLock,
  faLockOpen,
  faPencilAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "universal-cookie";

// api requests
import { getDean } from "../../PageComponents/AdminDashboard/AdminDashboardComponents/ModalSettings/deanApi";
import { getProjectInfosAndDepartments } from "./ApiRequests";
// pdf components
import {
  firstInputFormData,
  secondInputFormData,
} from "../../InputComponents/PDF/data";

//utils
import formatFirstFormDataForPDF from "../../../utils/formatFirstFormDataForPDF";
import formatSecondFormDataForPDF from "../../../utils/formatSecondFormDataForPDF";

const AdminDashboard = () => {
  const [, setProjectSets] = useState(null);

  // forms that hold form's data
  // each one holds project applications
  const [intentionForms, setIntentionForms] = useState(null);
  const [approvalForms, setApprovalForms] = useState(null);
  const [draftForms, setDraftForms] = useState(null);

  const [intentionSelection, setIntentionSelection] = useState(
    localStorage.getItem("selectedProject") === "intention",
  );
  const [approvalSelection, setApprovalSelection] = useState(
    localStorage.getItem("selectedProject") === "approval",
  );
  const [draftSelection, setDraftSelection] = useState(
    localStorage.getItem("selectedProject") === "draft",
  );

  // selected project inside second section
  // project is part of one of the groups -> either OBRASCI NAMJERE or TRAŽENJE SUGLASNOSTI
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  // pending, approved buttons
  const [pendingSelected, setPendingSelected] = useState(
    localStorage.getItem("projectTab") === "pending",
  );
  const [approvedSelected, setApprovedSelected] = useState(
    localStorage.getItem("projectTab") === "approved",
  );
  const [declinedSelected, setDeclinedSelected] = useState(
    localStorage.getItem("projectTab") === "declined",
  );
  const [dean, setDean] = useState({});

  const [pendingIntentionFormList, setPendingIntentionFormList] =
    useState(null);
  const [approvedIntentionFormList, setApprovedIntentionFormList] =
    useState(null);
  const [declinedIntentionFormList, setDeclinedIntentionFormList] =
    useState(null);

  const [pendingApprovalFormList, setPendingApprovalFormList] = useState(null);
  const [approvedApprovalFormList, setApprovedApprovalFormList] =
    useState(null);
  const [declinedApprovalFormList, setDeclinedApprovalFormList] =
    useState(null);

  // modal - settings
  const [modalSettingsIsOpen, setModalSettingsIsOpen] = useState(false);

  // modal - update project info
  const [modalUpdateProjectInfoIsOpen, setModalUpdateProjectInfoIsOpen] =
    useState(false);

  // modal - discard changes
  const [modalDiscardChangesIsOpen, setModalDiscardChangesIsOpen] =
    useState(false);

  // modal - approve project
  const [modalApproveProjectIsOpen, setModalApproveProjectIsOpen] =
    useState(false);

  // modal - decline project
  const [modalDeclineProjectIsOpen, setModalDeclineProjectIsOpen] =
    useState(false);

  // modal - message
  const [modalMessageIsOpen, setModalMessageIsOpen] = useState(false);
  const [messageForModal, setMessageForModal] = useState(
    "Projekt uspješno odobren!",
  );

  // modal (no reload)
  const [modalMessageNoReloadIsOpen, setModalMessageNoReloadIsOpen] =
    useState(false);
  const [messageForNoReloadModal, setMessageForNoReloadModal] = useState(
    "Projekt uspješno ažuriran!",
  );
  // loading spinner
  const [loadingSpinnerIsOpen, setLoadingSpinnerIsOpen] = useState(false);

  // project editable
  const [projectEditable, setProjectEditable] = useState(false);
  const [isApprovedProject, setIsApprovedProject] = useState(false);
  const [updateProjectData, setUpdateProjectData] = useState({});

  // data used to generate PDF out of selected project
  const [formattedData, setFormattedData] = useState(firstInputFormData);
  const [formattedData2, setFormattedData2] = useState(secondInputFormData);

  // project locked
  const [projectLocked, setProjectLocked] = useState(true);

  // editing of project in progress
  const [editingInProgress, setEditingInProgress] = useState(false);
  const [departments, setDepartments] = useState([]);

  // admin and moderator
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("selectedProject")) {
      localStorage.setItem("selectedProject", "intention");
      setIntentionSelection(true);
    }

    if (!localStorage.getItem("projectTab")) {
      localStorage.setItem("projectTab", "pending");
      setPendingSelected(true);
    }

    const fetchDataAndSetState = async () => {
      try {
        const { data, error } = await getProjectInfosAndDepartments();

        if (error === "UNAUTHORIZED") navigate(-1);

        const dean = await getDean();

        const { projectInfos, formattedDepartments } = data;
        return { projectInfos, formattedDepartments, dean: dean[0] };
      } catch (error) {
        console.error("Error fetching projectInfos");
      }
    };

    fetchDataAndSetState()
      .then(({ projectInfos, formattedDepartments, dean }) => {
        setProjectSets(projectInfos);
        setDepartments(formattedDepartments);

        setIntentionForms(
          projectInfos.filter(
            (item) =>
              item.firstInputMarker &&
              !item.secondInputMarker &&
              (item.state === "firstFormSubmitted" ||
                item.state === "firstFormApproved" ||
                item.state === "projectRejected"),
          ),
        );

        setApprovalForms(
          projectInfos.filter(
            (item) =>
              item.firstInputMarker === true &&
              item.secondInputMarker === true &&
              (item.state === "secondFormSubmitted" ||
                item.state === "secondFormApproved" ||
                item.state === "projectRejected"),
          ),
        );

        setDraftForms(projectInfos.filter((item) => item.state === "draft"));
        setDean(dean);
      })
      .catch((err) => console.error(err));
  }, []);

  // when INTENTION FORMS changes
  // filter them by state
  useEffect(() => {
    // OBRASCI NAMJERE
    setPendingIntentionFormList(
      intentionForms?.filter((item) => item.state === "firstFormSubmitted"),
    );
    setApprovedIntentionFormList(
      intentionForms?.filter((item) => item.state === "firstFormApproved"),
    );
    setDeclinedIntentionFormList(
      intentionForms?.filter((item) => item.state === "projectRejected"),
    );
  }, [intentionForms]);

  // when APPROVAL FORMS change
  // filter them by state
  useEffect(() => {
    // TRAŽENJE SUGLASNOSTI
    setPendingApprovalFormList(
      approvalForms?.filter((item) => item.state === "secondFormSubmitted"),
    );
    setApprovedApprovalFormList(
      approvalForms?.filter((item) => item.state === "secondFormApproved"),
    );
    setDeclinedApprovalFormList(
      approvalForms?.filter((item) => item.state === "projectRejected"),
    );
  }, [approvalForms]);

  // when selectedProject changes,
  // fill project's data into formattedData which will be used to generate PDF

  useEffect(() => {
    if (intentionSelection) {
      const newData = formatFirstFormDataForPDF(formattedData, selectedProject);
      setFormattedData(newData);
    } else if (approvalSelection) {
      const newData = formatSecondFormDataForPDF(
        formattedData2,
        selectedProject,
      );
      setFormattedData2(newData);
    }
  }, [
    selectedProject,
    intentionSelection,
    approvalSelection,
    formattedData,
    formattedData2,
  ]);

  useEffect(() => {
    const cookies = new Cookies(null, { path: "/" });
    const isModerator = cookies.get("isModerator");
    const isAdmin = cookies.get("isAdmin");

    setIsAdmin(isAdmin);
    setIsModerator(isModerator);
  }, []);

  // FUNCTIONS
  const handleEditable = () => {
    setProjectEditable(!projectEditable);
  };

  const handlePending = () => {
    localStorage.setItem("projectTab", "pending");
    setPendingSelected(true);
    setApprovedSelected(false);
    setDeclinedSelected(false);
  };

  const handleApproved = () => {
    localStorage.setItem("projectTab", "approved");
    setApprovedSelected(true);
    setPendingSelected(false);
    setDeclinedSelected(false);
  };

  const handleDeclined = () => {
    localStorage.setItem("projectTab", "declined");
    setApprovedSelected(false);
    setPendingSelected(false);
    setDeclinedSelected(true);
  };

  // button OBRASCI NAMJERE clickes
  const handleClickIntention = () => {
    if (editingInProgress) {
      setModalUpdateProjectInfoIsOpen(true);
    }
    setIntentionSelection(true);
    setApprovalSelection(false);
    setDraftSelection(false);
    localStorage.setItem("selectedProject", "intention");
    handlePending();
    setSelectedProject(null);
  };

  // button TRAŽENJE SUGLASNOSTI clicked
  const handleClickApproval = () => {
    if (editingInProgress) {
      setModalUpdateProjectInfoIsOpen(true);
    }
    setApprovalSelection(true);
    setIntentionSelection(false);
    setDraftSelection(false);
    localStorage.setItem("selectedProject", "approval");
    handlePending();
    setSelectedProject(null);
  };

  const handleClickDraft = () => {
    if (editingInProgress) {
      setModalUpdateProjectInfoIsOpen(true);
    }
    setDraftSelection(true);
    setApprovalSelection(false);
    setIntentionSelection(false);
    localStorage.setItem("selectedProject", "draft");
    handlePending();
    setSelectedProject(null);
  };

  // editing part
  const manageEditing = () => {
    setProjectEditable(true);
    setProjectLocked(false);
    setEditingInProgress(true);
  };

  const discardChanges = () => {
    setModalDiscardChangesIsOpen(true);
  };

  const handlePDF = async (projectData, title) => {
    try {
      // Dynamically import dependencies
      const { pdf } = await import("@react-pdf/renderer");
      const { default: InputFormPDF } =
        await import("../../InputComponents/PDF/InputFormPDF");

      const isProjectApproved = selectedProject.state === "secondFormApproved";

      const doc = (
        <InputFormPDF
          data={projectData}
          title={title}
          deanName={dean?.name}
          isProjectApproved={approvalSelection && isProjectApproved}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedProject?.nameSurname} - ${selectedProject?.projectTitle}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating PDF:", err);
    }
  };

  return (
    <>
      <AdminDashboardContext.Provider
        value={{
          setProjectSets,
          selectedProjectId,
          setSelectedProjectId,
          projectEditable,
          setProjectEditable,
          modalSettingsIsOpen,
          setModalSettingsIsOpen,
          isApprovedProject,
          setIsApprovedProject,
          intentionSelection,
          draftSelection,
          intentionForms,
          approvalForms,
          draftForms,
          approvalSelection,
          handleEditable,
          modalUpdateProjectInfoIsOpen,
          setModalUpdateProjectInfoIsOpen,
          updateProjectData,
          setUpdateProjectData,
          selectedProject,
          setSelectedProject,
          setProjectLocked,
          projectLocked,
          modalDiscardChangesIsOpen,
          setModalDiscardChangesIsOpen,
          modalApproveProjectIsOpen,
          setModalApproveProjectIsOpen,
          modalDeclineProjectIsOpen,
          setModalDeclineProjectIsOpen,
          editingInProgress,
          setEditingInProgress,
          setModalMessageIsOpen,
          setMessageForModal,
          setLoadingSpinnerIsOpen,
          setModalMessageNoReloadIsOpen,
          setMessageForNoReloadModal,
        }}
      >
        <div className="admin-dashboard-container">
          {/* MODAL COMPONENTS */}
          {modalSettingsIsOpen && <ModalSettings />}

          {modalUpdateProjectInfoIsOpen && <ModalUpdateProjectInfo />}

          {modalDiscardChangesIsOpen && <ModalDiscardChanges />}

          {modalApproveProjectIsOpen && <ModalApproveProject />}

          {modalDeclineProjectIsOpen && <ModalDeclineProject />}

          {modalMessageIsOpen && (
            <ModalMessage
              modalMessage={messageForModal}
              setModalIsOpen={setModalMessageIsOpen}
              reload={true}
            />
          )}

          {modalMessageNoReloadIsOpen && (
            <ModalMessage
              modalMessage={messageForNoReloadModal}
              setModalIsOpen={setModalMessageNoReloadIsOpen}
              reload={false}
            />
          )}

          {loadingSpinnerIsOpen && <LoadingSpinner />}

          <AdminDashboardHeader />

          <div className="admin-dashboard">
            {/* FIRST SECTION */}
            <div className="type-of-project-applications-section">
              <h5 className="section-title">Pretraga projekata</h5>
              {/* part that holds  */}
              <div className="type-of-project-applications-container">
                <button
                  className={`${
                    intentionSelection
                      ? "project-type-form-button-selected"
                      : "project-type-form-button"
                  }`}
                  onClick={handleClickIntention}
                >
                  <span>Obrasci namjere ({intentionForms?.length})</span>
                </button>

                <button
                  className={`${
                    approvalSelection
                      ? "project-type-form-button-selected"
                      : "project-type-form-button"
                  }`}
                  onClick={handleClickApproval}
                >
                  <span>Traženje suglasnosti ({approvalForms?.length})</span>
                </button>
                <button
                  className={`${
                    draftSelection
                      ? "project-type-form-button-selected"
                      : "project-type-form-button"
                  }`}
                  onClick={handleClickDraft}
                >
                  <span>Draft ({draftForms?.length})</span>
                </button>
              </div>
            </div>
            {/* SECOND SECTION */}
            <div className="project-applications-container">
              {/* PENDING, APPROVED AND DECLINED FILTER*/}

              {!draftSelection && (
                <div className="progress-container">
                  <button
                    onClick={handlePending}
                    className={
                      pendingSelected
                        ? "pending-button-selected"
                        : "pending-button-hidden"
                    }
                  >
                    U TIJEKU (
                    {intentionSelection
                      ? pendingIntentionFormList?.length
                      : pendingApprovalFormList?.length}
                    )
                  </button>
                  <button
                    onClick={handleApproved}
                    className={
                      approvedSelected
                        ? "approved-button-selected"
                        : "approved-button-hidden"
                    }
                  >
                    ODOBRENO (
                    {intentionSelection
                      ? approvedIntentionFormList?.length
                      : approvedApprovalFormList?.length}
                    )
                  </button>
                  <button
                    onClick={handleDeclined}
                    className={
                      declinedSelected
                        ? "declined-button-selected"
                        : "declined-button-hidden"
                    }
                  >
                    ODBIJENO (
                    {intentionSelection
                      ? declinedIntentionFormList?.length
                      : declinedApprovalFormList?.length}
                    )
                  </button>
                </div>
              )}
              {/* OBRASCI NAMJERE */}

              {draftSelection && (
                <ProjectInfoButtonContainer projectInfoSets={draftForms} />
              )}

              {intentionSelection && pendingSelected && (
                <ProjectInfoButtonContainer
                  projectInfoSets={pendingIntentionFormList}
                />
              )}
              {intentionSelection && approvedSelected && (
                <ProjectInfoButtonContainer
                  projectInfoSets={approvedIntentionFormList}
                />
              )}
              {intentionSelection && declinedSelected && (
                <ProjectInfoButtonContainer
                  projectInfoSets={declinedIntentionFormList}
                />
              )}

              {/* TRAŽENJE SUGLASNOSTI */}
              {approvalSelection && pendingSelected && (
                <ProjectInfoButtonContainer
                  projectInfoSets={pendingApprovalFormList}
                />
              )}
              {approvalSelection && approvedSelected && (
                <ProjectInfoButtonContainer
                  projectInfoSets={approvedApprovalFormList}
                />
              )}
              {approvalSelection && declinedSelected && (
                <ProjectInfoButtonContainer
                  projectInfoSets={declinedApprovalFormList}
                />
              )}
            </div>
            {/* THIRD SECTION */}
            {/* if selectedProject exists => show selected project's info */}
            <div
              className={
                projectLocked
                  ? "single-application-container-locked"
                  : "single-application-container-unlocked"
              }
            >
              {isAdmin &&
                !isModerator &&
                selectedProject &&
                pendingSelected && (
                  <>
                    {projectLocked ? (
                      <FontAwesomeIcon
                        icon={faLock}
                        style={{ alignSelf: "flex-start", marginBottom: "5px" }}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faLockOpen}
                        style={{ alignSelf: "flex-start", marginBottom: "5px" }}
                      />
                    )}

                    <div className="manage-project-container">
                      <div
                        className="edit-button-container manage-button-style"
                        onClick={
                          !projectEditable
                            ? () => manageEditing()
                            : () => setModalUpdateProjectInfoIsOpen(true)
                        }
                      >
                        <FontAwesomeIcon
                          icon={faPencilAlt}
                          style={{ color: "#FDF9F9" }}
                        />
                        <button className="edit-button">
                          {projectEditable ? "ZAVRŠI UREĐIVANJE" : "UREDI"}
                        </button>
                      </div>

                      {projectEditable && !projectLocked && (
                        <button
                          className="discard-changes-button manage-button-style"
                          onClick={() => discardChanges()}
                        >
                          ODBACI PROMJENE
                        </button>
                      )}

                      {!projectEditable && !draftSelection && (
                        <div className="decision-buttons-container">
                          <button
                            className="approve-button manage-button-style"
                            onClick={() => setModalApproveProjectIsOpen(true)}
                          >
                            ODOBRI
                          </button>

                          <button
                            className="decline-button manage-button-style"
                            onClick={() => setModalDeclineProjectIsOpen(true)}
                          >
                            ODBIJ
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              {selectedProject ? (
                <>
                  {/* PDF SECTION */}
                  {!draftSelection && (
                    <div className="project-to-pdf-container">
                      <p style={{ color: "#515151", fontWeight: "bold" }}>
                        Dohvati projekt u obliku pdf dokumenta:
                      </p>
                      <button
                        onClick={
                          intentionSelection
                            ? () => handlePDF(formattedData, "NAMJERA PRIJAVE")
                            : () =>
                                handlePDF(
                                  formattedData2,
                                  "TRAŽENJE SUGLASNOSTI",
                                )
                        }
                        className="project-to-pdf-button"
                      >
                        <p className="project-to-pdf-txt">PREUZMI</p>
                        <FontAwesomeIcon
                          icon={faDownload}
                          style={{ color: "#ffffff" }}
                        />
                      </button>
                    </div>
                  )}
                  {approvalSelection && selectedProject && (
                    <Link to={`/SecondInput/${selectedProject.id}`}>
                      Link na formu traženja suglasnosti
                    </Link>
                  )}

                  {/* PDF SECTION END */}
                  <ProjectInfo
                    selectedProject={selectedProject}
                    departments={departments}
                  />
                </>
              ) : (
                <h3>Nema odabranog projekta</h3>
              )}
            </div>
          </div>
        </div>
      </AdminDashboardContext.Provider>
    </>
  );
};

export default AdminDashboard;
